/**
 * Captura de guía de despacho.
 *
 * Pensado para usarse en el teléfono, en planta, con guantes y sin señal:
 * seis botones grandes que estampan la hora con un toque, autocompletado
 * desde los catálogos, y el cálculo de sobrestadía y recargos a la vista
 * mientras se carga — no tres meses después.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, Clock, RotateCcw, Save, TriangleAlert, X } from 'lucide-react';
import { Boton, Campo, Chip, Selector, Tarjeta, TarjetaTitulo, type Nivel } from '@/components/ui';
import { useCatalogos } from '@/hooks/useCatalogos';
import { COL } from '@/db/firebase';
import { guardar, folioOcupado, ultimoFolio, leerGuia } from '@/db/repositorio';
import { metaNueva, uuidv7, type DocGuia } from '@/db/tiposDatos';
import { calcularTiempos, formatearMinutos, nivelEstadia } from '@/domain/tiempos';
import { calcularRecargos, totalRecargosUf } from '@/domain/recargos';
import { parsearProducto, describirProducto } from '@/domain/producto';
import { aFechaISO, uf, m3 as fm3 } from '@/domain/formato';
import type { CodigoPlanta, Turno } from '@/domain/tipos';
import { cn } from '@/lib/cn';

type ClaveMarca =
  | 'tsSalidaPlanta'
  | 'tsLlegadaObra'
  | 'tsInicioDescarga'
  | 'tsTerminoDescarga'
  | 'tsSalidaObra'
  | 'tsLlegadaPlanta';

const MARCAS: { clave: ClaveMarca; texto: string; corto: string }[] = [
  { clave: 'tsSalidaPlanta', texto: 'Salida de planta', corto: 'Salida planta' },
  { clave: 'tsLlegadaObra', texto: 'Llegada a obra', corto: 'Llegada obra' },
  { clave: 'tsInicioDescarga', texto: 'Inicio de descarga', corto: 'Inicio desc.' },
  { clave: 'tsTerminoDescarga', texto: 'Término de descarga', corto: 'Fin desc.' },
  { clave: 'tsSalidaObra', texto: 'Salida de obra', corto: 'Salida obra' },
  { clave: 'tsLlegadaPlanta', texto: 'Llegada a planta', corto: 'Llegada planta' },
];

type Formulario = {
  plantaCodigo: CodigoPlanta;
  folio: string;
  remito: string;
  fecha: string;
  turno: Turno;
  clienteId: string;
  productoId: string;
  cantidadM3: string;
  centroCostoId: string;
  destinoId: string;
  destinoTexto: string;
  equipoId: string;
  operadorMixerId: string;
  operadorPlantaId: string;
  solicitante: string;
  observaciones: string;
  itemContratoCodigo: string;
} & Record<ClaveMarca, string>;

const VACIO: Formulario = {
  plantaCodigo: 'KM 13',
  folio: '',
  remito: '',
  fecha: aFechaISO(new Date()),
  turno: 'A',
  clienteId: '',
  productoId: '',
  cantidadM3: '',
  centroCostoId: '',
  destinoId: '',
  destinoTexto: '',
  equipoId: '',
  operadorMixerId: '',
  operadorPlantaId: '',
  solicitante: '',
  observaciones: '',
  itemContratoCodigo: '',
  tsSalidaPlanta: '',
  tsLlegadaObra: '',
  tsInicioDescarga: '',
  tsTerminoDescarga: '',
  tsSalidaObra: '',
  tsLlegadaPlanta: '',
};

/** 'HH:mm' + fecha base → instante. Si retrocede respecto del anterior, cruzó medianoche. */
function aInstante(fecha: string, hhmm: string, previo: Date | null): Date | null {
  if (!hhmm) return null;
  const [hh, mm] = hhmm.split(':').map(Number);
  if (hh === undefined || mm === undefined) return null;
  const [a, me, d] = fecha.split('-').map(Number);
  const inst = new Date(a!, me! - 1, d!, hh, mm, 0, 0);
  if (previo && inst < previo) inst.setDate(inst.getDate() + 1);
  return inst;
}

function horaAhora(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export default function NuevaGuia() {
  const { catalogos, buscarItem } = useCatalogos();
  const navegar = useNavigate();
  const { id: idRuta } = useParams();

  const [f, setF] = useState<Formulario>(VACIO);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState<{ nivel: Nivel; texto: string } | null>(null);
  const [errorFolio, setErrorFolio] = useState<string | null>(null);
  const [ahora, setAhora] = useState(Date.now());
  const idGuia = useRef<string>(idRuta ?? uuidv7());

  const set = <K extends keyof Formulario>(k: K, v: Formulario[K]) =>
    setF((prev) => ({ ...prev, [k]: v }));

  // Reloj para el cronómetro en vivo del viaje en curso.
  useEffect(() => {
    const t = setInterval(() => setAhora(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);

  // Propone el folio siguiente al cambiar de planta.
  useEffect(() => {
    if (idRuta) return;
    void (async () => {
      const ultimo = await ultimoFolio(f.plantaCodigo);
      if (ultimo) setF((p) => (p.folio ? p : { ...p, folio: String(ultimo + 1) }));
    })();
  }, [f.plantaCodigo, idRuta]);

  // Carga la guía si venimos a editar.
  useEffect(() => {
    if (!idRuta) return;
    void (async () => {
      const g = await leerGuia(idRuta);
      if (!g) return;
      const hora = (iso: string | null) =>
        iso
          ? `${String(new Date(iso).getHours()).padStart(2, '0')}:${String(new Date(iso).getMinutes()).padStart(2, '0')}`
          : '';
      setF({
        plantaCodigo: g.plantaCodigo,
        folio: String(g.folio),
        remito: g.remito ? String(g.remito) : '',
        fecha: g.fecha,
        turno: g.turno ?? 'A',
        clienteId: g.clienteId ?? '',
        productoId: g.productoId ?? '',
        cantidadM3: String(g.cantidadM3),
        centroCostoId: g.centroCostoId ?? '',
        destinoId: g.destinoId ?? '',
        destinoTexto: g.destinoTexto ?? '',
        equipoId: g.equipoId ?? '',
        operadorMixerId: g.operadorMixerId ?? '',
        operadorPlantaId: g.operadorPlantaId ?? '',
        solicitante: g.solicitante ?? '',
        observaciones: g.observaciones ?? '',
        itemContratoCodigo: g.itemContratoCodigo ?? '',
        tsSalidaPlanta: hora(g.tsSalidaPlanta),
        tsLlegadaObra: hora(g.tsLlegadaObra),
        tsInicioDescarga: hora(g.tsInicioDescarga),
        tsTerminoDescarga: hora(g.tsTerminoDescarga),
        tsSalidaObra: hora(g.tsSalidaObra),
        tsLlegadaPlanta: hora(g.tsLlegadaPlanta),
      });
    })();
  }, [idRuta]);

  // ── Derivados en vivo ───────────────────────────────────────────────────
  const planta = catalogos.plantas.find((p) => p.codigoPlanta === f.plantaCodigo);
  const producto = catalogos.productos.find((p) => p.id === f.productoId);
  const cantidad = Number(f.cantidadM3.replace(',', '.')) || 0;

  const instantes = useMemo(() => {
    let previo: Date | null = null;
    const out: Record<string, Date | undefined> = {};
    for (const { clave } of MARCAS) {
      const inst = aInstante(f.fecha, f[clave], previo);
      if (inst) {
        out[clave] = inst;
        previo = inst;
      }
    }
    return out;
  }, [f]);

  const tiempos = useMemo(
    () =>
      calcularTiempos({
        salidaPlanta: instantes.tsSalidaPlanta,
        llegadaObra: instantes.tsLlegadaObra,
        inicioDescarga: instantes.tsInicioDescarga,
        terminoDescarga: instantes.tsTerminoDescarga,
        salidaObra: instantes.tsSalidaObra,
        llegadaPlanta: instantes.tsLlegadaPlanta,
      }),
    [instantes],
  );

  const recargos = useMemo(() => {
    if (!planta || !producto) return [];
    const p = parsearProducto(producto.codigo);
    return calcularRecargos(
      {
        plantaCodigo: f.plantaCodigo,
        minCargaM3: planta.minCargaM3,
        cantidadM3: cantidad,
        producto: p,
        generaRecargos: producto.generaRecargos,
        tiempos,
        codigoProductoNormalizado: producto.codigoNormalizado,
      },
      buscarItem,
    );
  }, [planta, producto, cantidad, tiempos, f.plantaCodigo, buscarItem]);

  const itemPrincipal = f.itemContratoCodigo ? buscarItem(f.itemContratoCodigo) : null;
  const montoPrincipalUf = itemPrincipal ? cantidad * itemPrincipal.precioUf : 0;
  const totalUf = montoPrincipalUf + totalRecargosUf(recargos);

  /** Estadía en curso: hay llegada a obra pero aún no salida. */
  const estadiaEnCurso = useMemo(() => {
    const llegada = instantes.tsLlegadaObra;
    if (!llegada || instantes.tsSalidaObra) return null;
    return Math.max(0, Math.round((ahora - llegada.getTime()) / 60000));
  }, [instantes, ahora]);

  // ── Validación de folio ─────────────────────────────────────────────────
  useEffect(() => {
    const folio = Number(f.folio);
    if (!folio) {
      setErrorFolio(null);
      return;
    }
    let cancelado = false;
    void (async () => {
      const ocupado = await folioOcupado(f.plantaCodigo, folio, idGuia.current);
      if (!cancelado) {
        setErrorFolio(
          ocupado ? `El folio ${folio} ya existe en ${f.plantaCodigo} (${ocupado.fecha}).` : null,
        );
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [f.folio, f.plantaCodigo]);

  const marcar = (clave: ClaveMarca) => set(clave, horaAhora());
  const limpiarMarca = (clave: ClaveMarca) => set(clave, '');

  // ── Guardar ─────────────────────────────────────────────────────────────
  async function alGuardar(emitir: boolean) {
    if (!f.folio) return setMensaje({ nivel: 'ROJO', texto: 'Falta el número de guía.' });
    if (errorFolio) return setMensaje({ nivel: 'ROJO', texto: errorFolio });
    if (!f.clienteId) return setMensaje({ nivel: 'ROJO', texto: 'Falta el cliente.' });
    if (!f.productoId) return setMensaje({ nivel: 'ROJO', texto: 'Falta el producto.' });
    if (emitir && cantidad <= 0)
      return setMensaje({ nivel: 'ROJO', texto: 'La cantidad debe ser mayor que cero.' });

    setGuardando(true);
    try {
      const cliente = catalogos.clientes.find((c) => c.id === f.clienteId);
      const centro = catalogos.centrosCosto.find((c) => c.id === f.centroCostoId);
      const equipo = catalogos.equipos.find((e) => e.id === f.equipoId);
      const opMixer = catalogos.personas.find((p) => p.id === f.operadorMixerId);
      const opPlanta = catalogos.personas.find((p) => p.id === f.operadorPlantaId);
      const destino = catalogos.destinos.find((d) => d.id === f.destinoId);
      const iso = (k: ClaveMarca) => instantes[k]?.toISOString() ?? null;

      const doc: DocGuia = {
        ...metaNueva(null),
        id: idGuia.current,
        plantaId: planta?.id ?? '',
        plantaCodigo: f.plantaCodigo,
        sector: planta?.sector ?? 'SUPERFICIE',
        folio: Number(f.folio),
        remito: f.remito ? Number(f.remito) : null,
        fecha: f.fecha,
        turno: f.turno,
        clienteId: f.clienteId || null,
        clienteNombre: cliente?.nombre ?? null,
        productoId: f.productoId || null,
        productoCodigo: producto?.codigo ?? null,
        cantidadM3: cantidad,
        centroCostoId: f.centroCostoId || null,
        centroCostoCodigo: centro?.codigo ?? null,
        proyectoId: null,
        proyectoNombre: null,
        destinoId: f.destinoId || null,
        destinoTexto: destino?.nombre ?? (f.destinoTexto || null),
        equipoId: f.equipoId || null,
        equipoCodigo: equipo?.codigo ?? null,
        operadorMixerId: f.operadorMixerId || null,
        operadorMixerNombre: opMixer?.nombre ?? null,
        operadorPlantaId: f.operadorPlantaId || null,
        operadorPlantaNombre: opPlanta?.nombre ?? null,
        solicitante: f.solicitante || null,
        observaciones: f.observaciones || null,
        tsSalidaPlanta: iso('tsSalidaPlanta'),
        tsLlegadaObra: iso('tsLlegadaObra'),
        tsInicioDescarga: iso('tsInicioDescarga'),
        tsTerminoDescarga: iso('tsTerminoDescarga'),
        tsSalidaObra: iso('tsSalidaObra'),
        tsLlegadaPlanta: iso('tsLlegadaPlanta'),
        itemContratoId: itemPrincipal?.id ?? null,
        itemContratoCodigo: f.itemContratoCodigo || null,
        estado: emitir
          ? producto && !producto.parseOk
            ? 'REQUIERE_CLASIFICACION'
            : 'EMITIDA'
          : 'BORRADOR',
        motivoAnulacion: null,
        estadoPagoId: null,
        origen: 'APP',
        minEstadiaObra: tiempos.estadiaObra,
        minSobreEstadia: tiempos.sobreEstadia,
        bloquesCodelco: tiempos.bloquesCodelco,
        tiemposCompletos: tiempos.completa,
        montoRecargosUf: totalRecargosUf(recargos),
      };

      await guardar(COL.guias, doc);
      setMensaje({
        nivel: 'VERDE',
        texto: emitir ? `Guía ${doc.folio} emitida.` : `Guía ${doc.folio} guardada como borrador.`,
      });

      if (emitir) {
        setTimeout(() => navegar('/guias'), 700);
      }
    } catch (e) {
      setMensaje({
        nivel: 'ROJO',
        texto: e instanceof Error ? e.message : 'No se pudo guardar la guía.',
      });
    } finally {
      setGuardando(false);
    }
  }

  function limpiar() {
    idGuia.current = uuidv7();
    setF({ ...VACIO, plantaCodigo: f.plantaCodigo, fecha: f.fecha, turno: f.turno });
    setMensaje(null);
  }

  const nivelCrono: Nivel = estadiaEnCurso === null ? 'NEUTRO' : nivelEstadia(estadiaEnCurso);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">{idRuta ? 'Editar guía' : 'Nueva guía'}</h1>
          <p className="text-xs text-texto-mute">
            Los recargos y la sobrestadía se calculan solos mientras cargas.
          </p>
        </div>
        <div className="flex gap-2">
          <Boton variante="fantasma" icono={<RotateCcw size={15} />} onClick={limpiar}>
            Limpiar
          </Boton>
          <Boton
            variante="secundario"
            icono={<Save size={15} />}
            onClick={() => void alGuardar(false)}
            disabled={guardando}
          >
            Borrador
          </Boton>
          <Boton
            variante="primario"
            icono={<Check size={15} />}
            onClick={() => void alGuardar(true)}
            disabled={guardando}
          >
            Emitir guía
          </Boton>
        </div>
      </div>

      {mensaje && (
        <div
          className={cn(
            'rounded-tarjeta border px-4 py-3 text-sm',
            mensaje.nivel === 'VERDE' && 'border-fluor-verde/40 bg-fluor-verde/10 text-fluor-verde',
            mensaje.nivel === 'ROJO' && 'border-fluor-rojo/40 bg-fluor-rojo/10 text-fluor-rojo',
          )}
        >
          {mensaje.texto}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* ── Columna principal ───────────────────────────────────────── */}
        <div className="space-y-4 lg:col-span-2">
          <Tarjeta>
            <TarjetaTitulo>Identificación</TarjetaTitulo>
            <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
              <Selector
                etiqueta="Planta"
                name="plantaCodigo"
                value={f.plantaCodigo}
                onChange={(e) => set('plantaCodigo', e.target.value as CodigoPlanta)}
              >
                {catalogos.plantas.map((p) => (
                  <option key={p.id} value={p.codigoPlanta}>
                    {p.codigoPlanta} — {p.sector}
                  </option>
                ))}
              </Selector>

              <Campo
                etiqueta="N° de guía"
                inputMode="numeric"
                name="folio"
                value={f.folio}
                onChange={(e) => set('folio', e.target.value.replace(/\D/g, ''))}
                error={errorFolio ?? undefined}
                className="font-mono"
              />

              <Campo
                etiqueta="N° remito"
                inputMode="numeric"
                name="remito"
                value={f.remito}
                onChange={(e) => set('remito', e.target.value.replace(/\D/g, ''))}
                className="font-mono"
              />

              <Campo
                etiqueta="Fecha"
                type="date"
                name="fecha"
                value={f.fecha}
                onChange={(e) => set('fecha', e.target.value)}
              />

              <Selector
                etiqueta="Turno"
                name="turno"
                value={f.turno}
                onChange={(e) => set('turno', e.target.value as Turno)}
              >
                <option value="A">A — día</option>
                <option value="C">C — noche</option>
                <option value="TA">TA — adicional</option>
              </Selector>

              <Selector
                etiqueta="Cliente"
                name="clienteId"
                value={f.clienteId}
                onChange={(e) => set('clienteId', e.target.value)}
              >
                <option value="">Seleccionar…</option>
                {catalogos.clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </Selector>
            </div>
          </Tarjeta>

          <Tarjeta>
            <TarjetaTitulo>Carga</TarjetaTitulo>
            <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
              <Selector
                etiqueta="Producto"
                name="productoId"
                value={f.productoId}
                onChange={(e) => set('productoId', e.target.value)}
                className="sm:col-span-2"
              >
                <option value="">Seleccionar…</option>
                {catalogos.productos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.codigo}
                    {!p.parseOk ? '  (sin clasificar)' : ''}
                  </option>
                ))}
              </Selector>

              <Campo
                etiqueta="Cantidad"
                inputMode="decimal"
                sufijo="m³"
                name="cantidadM3"
                value={f.cantidadM3}
                onChange={(e) => set('cantidadM3', e.target.value.replace(/[^\d.,]/g, ''))}
                className="font-mono"
              />

              <Selector
                etiqueta="Partida del contrato"
                name="itemContratoCodigo"
                value={f.itemContratoCodigo}
                onChange={(e) => set('itemContratoCodigo', e.target.value)}
                className="sm:col-span-2 lg:col-span-3"
              >
                <option value="">Sin asignar</option>
                {catalogos.items
                  .filter((i) => !i.esRecargo)
                  .map((i) => (
                    <option key={i.id} value={i.codigo}>
                      {i.codigo} — {i.descripcion} ({uf(i.precioUf)} UF/{i.unidad})
                    </option>
                  ))}
              </Selector>

              <Selector
                etiqueta="Centro de costo / grafo"
                name="centroCostoId"
                value={f.centroCostoId}
                onChange={(e) => set('centroCostoId', e.target.value)}
              >
                <option value="">Seleccionar…</option>
                {catalogos.centrosCosto.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.codigo}
                  </option>
                ))}
              </Selector>

              <Selector
                etiqueta="Destino"
                name="destinoId"
                value={f.destinoId}
                onChange={(e) => set('destinoId', e.target.value)}
                className="sm:col-span-2"
              >
                <option value="">Seleccionar…</option>
                {catalogos.destinos.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.nombre}
                  </option>
                ))}
              </Selector>

              {producto && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <Chip nivel={producto.parseOk ? 'NEUTRO' : 'AMARILLO'}>
                    {producto.parseOk
                      ? describirProducto(parsearProducto(producto.codigo))
                      : 'Producto sin clasificar: quedará marcado para revisión'}
                  </Chip>
                </div>
              )}
            </div>
          </Tarjeta>

          {/* ── Marcas de tiempo ──────────────────────────────────────── */}
          <Tarjeta>
            <TarjetaTitulo
              accion={
                estadiaEnCurso !== null ? (
                  <Chip nivel={nivelCrono}>
                    <Clock size={12} /> En obra {formatearMinutos(estadiaEnCurso)}
                  </Chip>
                ) : undefined
              }
            >
              Ciclo del viaje
            </TarjetaTitulo>
            <div className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3">
              {MARCAS.map(({ clave, texto, corto }) => {
                const puesta = Boolean(f[clave]);
                return (
                  <div
                    key={clave}
                    className={cn(
                      'rounded-control border p-2.5 transition-colors',
                      puesta
                        ? 'border-fluor-verde/35 bg-fluor-verde/5'
                        : 'border-linea bg-petroleo-800/40',
                    )}
                  >
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <span className="text-[11px] uppercase tracking-wide text-texto-mute">
                        <span className="sm:hidden">{corto}</span>
                        <span className="hidden sm:inline">{texto}</span>
                      </span>
                      {puesta && (
                        <button
                          type="button"
                          onClick={() => limpiarMarca(clave)}
                          className="text-texto-mute hover:text-fluor-rojo"
                          aria-label={`Borrar ${texto}`}
                        >
                          <X size={13} />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        name={clave}
                        value={f[clave]}
                        onChange={(e) => set(clave, e.target.value)}
                        className="min-h-tactil flex-1 rounded-control border border-linea bg-petroleo-800/60 px-2 font-mono text-sm text-texto focus:border-petroleo-300 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => marcar(clave)}
                        className="min-h-tactil min-w-tactil rounded-control bg-petroleo-500 px-3 text-xs font-semibold text-white transition-colors hover:bg-petroleo-400 active:scale-95"
                      >
                        Ahora
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {tiempos.anomalias.length > 0 && (
              <div className="border-t border-linea px-4 py-3">
                {tiempos.anomalias.map((a) => (
                  <div key={a} className="flex items-start gap-2 text-xs text-fluor-amarillo">
                    <TriangleAlert size={13} className="mt-0.5 shrink-0" />
                    {a}
                  </div>
                ))}
              </div>
            )}
          </Tarjeta>

          <Tarjeta>
            <TarjetaTitulo>Responsables</TarjetaTitulo>
            <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
              <Selector
                etiqueta="Equipo"
                name="equipoId"
                value={f.equipoId}
                onChange={(e) => set('equipoId', e.target.value)}
              >
                <option value="">Seleccionar…</option>
                {catalogos.equipos.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.codigo}
                  </option>
                ))}
              </Selector>
              <Selector
                etiqueta="Operador mixer"
                name="operadorMixerId"
                value={f.operadorMixerId}
                onChange={(e) => set('operadorMixerId', e.target.value)}
              >
                <option value="">Seleccionar…</option>
                {catalogos.personas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </Selector>
              <Selector
                etiqueta="Operador planta"
                name="operadorPlantaId"
                value={f.operadorPlantaId}
                onChange={(e) => set('operadorPlantaId', e.target.value)}
              >
                <option value="">Seleccionar…</option>
                {catalogos.personas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </Selector>
              <Campo
                etiqueta="Solicitado por"
                name="solicitante"
                value={f.solicitante}
                onChange={(e) => set('solicitante', e.target.value)}
              />
              <Campo
                etiqueta="Observaciones"
                name="observaciones"
                value={f.observaciones}
                onChange={(e) => set('observaciones', e.target.value)}
                className="sm:col-span-2 lg:col-span-4"
              />
            </div>
          </Tarjeta>
        </div>

        {/* ── Panel de cálculo en vivo ────────────────────────────────── */}
        <div className="space-y-4">
          <Tarjeta className="lg:sticky lg:top-32">
            <TarjetaTitulo>Cálculo en vivo</TarjetaTitulo>

            <div className="space-y-3 p-4">
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ['Traslado ida', tiempos.trasladoIda],
                  ['Espera en obra', tiempos.espera],
                  ['Descarga', tiempos.descarga],
                  ['Salida de obra', tiempos.salidaObra],
                  ['Traslado vuelta', tiempos.trasladoVuelta],
                  ['Ciclo total', tiempos.trasladoTotal],
                ].map(([et, v]) => (
                  <div key={et as string} className="rounded-control bg-petroleo-800/40 px-2.5 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-texto-mute">{et}</div>
                    <div className="font-mono text-sm tabular-nums">
                      {formatearMinutos(v as number | null)}
                    </div>
                  </div>
                ))}
              </div>

              <div
                className={cn(
                  'rounded-control border px-3 py-2.5',
                  tiempos.sobreEstadia && tiempos.sobreEstadia > 0
                    ? 'border-fluor-amarillo/35 bg-fluor-amarillo/10'
                    : 'border-linea bg-petroleo-800/40',
                )}
              >
                <div className="flex items-center justify-between text-xs">
                  <span className="text-texto-mute">Estadía en obra</span>
                  <span className="font-mono tabular-nums">
                    {formatearMinutos(tiempos.estadiaObra)}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between text-xs">
                  <span className="text-texto-mute">Sobre 45 min libres</span>
                  <span className="font-mono tabular-nums">
                    {formatearMinutos(tiempos.sobreEstadia)}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between text-xs">
                  <span className="text-texto-mute">Bloques de 10 min</span>
                  <span
                    className={cn(
                      'font-mono font-semibold tabular-nums',
                      tiempos.bloquesCodelco ? 'text-fluor-amarillo' : '',
                    )}
                  >
                    {tiempos.bloquesCodelco ?? '—'}
                  </span>
                </div>
              </div>

              {!tiempos.completa && (
                <div className="flex items-start gap-2 text-xs text-texto-mute">
                  <TriangleAlert size={13} className="mt-0.5 shrink-0 text-fluor-amarillo" />
                  Faltan horarios: sin los seis no se puede cobrar la sobrestadía.
                </div>
              )}

              <div className="border-t border-linea pt-3">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-texto-mute">
                  Valorización
                </div>

                {itemPrincipal && cantidad > 0 && (
                  <div className="flex items-start justify-between gap-2 py-1 text-xs">
                    <span className="text-texto-mute">
                      {itemPrincipal.codigo} · {fm3(cantidad)} m³
                    </span>
                    <span className="shrink-0 font-mono tabular-nums">
                      {uf(montoPrincipalUf)}
                    </span>
                  </div>
                )}

                {recargos.map((r) => (
                  <div
                    key={r.regla}
                    className="flex items-start justify-between gap-2 py-1 text-xs"
                    title={r.detalle}
                  >
                    <span className="text-fluor-amarillo">
                      {r.itemCodigo} · {r.regla.toLowerCase().replace(/_/g, ' ')}
                    </span>
                    <span className="shrink-0 font-mono tabular-nums">{uf(r.montoUf)}</span>
                  </div>
                ))}

                {!itemPrincipal && recargos.length === 0 && (
                  <p className="py-1 text-xs text-texto-mute">
                    Elige producto, cantidad y partida para ver la valorización.
                  </p>
                )}

                <div className="mt-2 flex items-center justify-between border-t border-linea pt-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-texto-mute">
                    Total guía
                  </span>
                  <span className="font-mono text-lg font-semibold tabular-nums text-fluor-verde">
                    {uf(totalUf)} <span className="text-xs text-texto-mute">UF</span>
                  </span>
                </div>
              </div>
            </div>
          </Tarjeta>
        </div>
      </div>
    </div>
  );
}
